import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Page } from '@/shared/ui/Page';
import { PageHeader } from '@/shared/ui/PageHeader';
import { BottomDock } from '@/shared/ui/BottomDock';
import { notificationApi } from '@/entities/notification/api';
import { useI18nStore, useT, type Lang } from '@/shared/lib/i18n';
import { cn } from '@/shared/lib/cn';

const LANGS: { value: Lang; flag: string; labelKey: string }[] = [
  { value: 'ru', flag: '🇷🇺', labelKey: 'settings.languageRu' },
  { value: 'en', flag: '🇬🇧', labelKey: 'settings.languageEn' },
];

const EVENTS = [
  'charge_created',
  'reminder',
  'price_changed',
  'payment_marked',
  'payment_confirmed',
  'payment_rejected',
  'receipt_requested',
  'receipt_uploaded',
  'exit_request_received',
  'exit_approved',
  'exit_declined',
  'manual_correction_created',
  'operation_reversed',
  'subscription_closed',
  'subscription_paused',
  'subscription_resumed',
  'invite_accepted',
  'member_force_removed',
  'participant_unreachable',
] as const;

function ToggleRow({
  label,
  enabled,
  disabled,
  onToggle,
}: {
  label: string;
  enabled: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      className="flex items-center gap-3 rounded-[12px] px-4 py-3 bg-white/50 border border-white/60 disabled:opacity-60"
      disabled={disabled}
      onClick={onToggle}
    >
      <span className="flex-1 text-left text-[14px] font-medium">{label}</span>
      <span className={cn(
        'w-[46px] h-[28px] rounded-full p-[3px] transition-colors',
        enabled ? 'bg-brand-green' : 'bg-black/10',
      )}>
        <span className={cn(
          'block w-[22px] h-[22px] rounded-full bg-white shadow-sm transition-transform',
          enabled && 'translate-x-[18px]',
        )} />
      </span>
    </button>
  );
}

export function SettingsPage() {
  const t = useT();
  const { lang, setLang } = useI18nStore();
  const qc = useQueryClient();

  const settingsQ = useQuery({
    queryKey: ['notification-settings'],
    queryFn: () => notificationApi.list(),
  });

  const mutation = useMutation({
    mutationFn: ({ event, enabled }: { event: string; enabled: boolean }) =>
      notificationApi.set(event, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notification-settings'] }),
  });

  const settings = settingsQ.data?.settings ?? {};

  return (
    <>
      <Page>
        <PageHeader title={<span className="font-bold text-[19px]">{t('settings.title')}</span>} />
        <div className="px-[18px] pb-[96px] flex flex-col gap-4 pt-2">
          <div>
            <p className="text-[13px] text-text-secondary mb-2">{t('settings.language')}</p>
            <div className="flex gap-2">
              {LANGS.map(({ value, flag, labelKey }) => (
                <button
                  key={value}
                  onClick={() => setLang(value)}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 rounded-[14px] py-3 text-[15px] font-medium transition-all',
                    lang === value
                      ? 'bg-brand-green/10 border-2 border-brand-green text-text-primary'
                      : 'glass-content text-text-secondary',
                  )}
                >
                  <span className="text-[20px]">{flag}</span>
                  <span>{t(labelKey)}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[14px] font-semibold text-text-secondary">{t('settings.notifications')}</p>
            <p className="text-[12px] text-text-muted mt-1">{t('settings.notificationsHint')}</p>
          </div>

          <div className="flex flex-col gap-2">
            {EVENTS.map((event) => {
              const enabled = settings[event] ?? true;
              return (
                <ToggleRow
                  key={event}
                  label={t(`settings.events.${event}`)}
                  enabled={enabled}
                  disabled={mutation.isPending}
                  onToggle={() => mutation.mutate({ event, enabled: !enabled })}
                />
              );
            })}
          </div>
        </div>
      </Page>
      <BottomDock />
    </>
  );
}
